export type RAGStatus = 'red' | 'amber' | 'green';

export const RAG_COLORS: Record<RAGStatus, { bg: string; text: string }> = {
  red: { bg: '#FFCCCB', text: '#990000' },
  amber: { bg: '#FFF2CC', text: '#CC8400' },
  green: { bg: '#D9EAD3', text: '#008000' },
};

/**
 * Returns the RAG status for a given metric and value.
 * Thresholds are sourced from the PrecisionX Sales Dashboard Requirements v3.3.
 *
 * Metric names:
 *   'score'              — composite score 0–100
 *   'p2p_percent'        — performance to plan % 0–100
 *   'p2p_pace'           — pace to plan % 0–100
 *   'quotes_per_week'    — count
 *   'closing_ratio'      — ratio 0.0–1.0
 *   'calls_today'        — count
 *   'daily_call_avg'     — count
 *   'connection_ratio'   — ratio 0.0–1.0
 *   'call_to_quote_ratio'— ratio 0.0–1.0
 *   'ignored_lead_ratio' — ratio 0.0–1.0
 */
export function getRAGStatus(metric: string, value: number): RAGStatus {
  switch (metric) {
    case 'score':
    case 'p2p_percent':
    case 'p2p_pace':
      if (value < 80) return 'red';
      if (value < 90) return 'amber';
      return 'green';

    case 'quotes_per_week':
      if (value <= 5) return 'red';
      if (value < 15) return 'amber';
      return 'green';

    case 'closing_ratio':
      if (value < 0.15) return 'red';
      if (value < 0.2) return 'amber';
      return 'green';

    case 'calls_today':
    case 'daily_call_avg':
      if (value <= 60) return 'red';
      if (value < 90) return 'amber';
      return 'green';

    case 'connection_ratio':
    case 'call_to_quote_ratio':
      if (value < 0.15) return 'red';
      if (value < 0.2) return 'amber';
      return 'green';

    case 'ignored_lead_ratio':
      if (value < 0.8) return 'red';
      if (value < 0.9) return 'amber';
      return 'green';

    default:
      return 'green';
  }
}

export function getRagColors(status: RAGStatus) {
  return RAG_COLORS[status];
}

/** Formats a ratio (0.0–1.0) as a percentage string, e.g. 0.22 → "22%" */
export function formatRatio(value: number): string {
  return `${Math.round(value * 100)}%`;
}

/** Formats a currency value, e.g. 58843 → "$58,843" */
export function formatCurrency(value: number): string {
  return `$${value.toLocaleString('en-CA', { maximumFractionDigits: 0 })}`;
}

/** Formats a YYYY-MM string as a human-readable month label.
 *  format='short' → "Jan '25"   format='long' → "January 2025"
 */
export function formatYearMonth(
  ym: string,
  format: 'short' | 'long' = 'long',
): string {
  if (!ym) return '';
  const [year, month] = ym.split('-');
  const d = new Date(Number(year), Number(month) - 1, 1);
  return format === 'short'
    ? d.toLocaleString('en-CA', { month: 'short', year: '2-digit' })
    : d.toLocaleString('en-CA', { month: 'long', year: 'numeric' });
}
